import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  Save,
  RotateCcw,
  ToggleLeft,
  Shield,
  Palette,
  Globe,
  Mail,
  Bell,
  Database,
  Key,
  Users,
  AlertTriangle
} from 'lucide-react';
import { api } from '../auth/authStore';

interface TenantSettings {
  id: string;
  name: string;
  config: {
    features: string[];
    customization: {
      theme: string;
      logo?: string;
      primaryColor?: string;
      secondaryColor?: string;
      customCss?: string;
    };
    security: {
      passwordPolicy: {
        minLength: number;
        requireUppercase: boolean;
        requireLowercase: boolean;
        requireNumbers: boolean;
        requireSymbols: boolean;
        maxAge: number;
      };
      sessionTimeout: number;
      mfaRequired: boolean;
      ipWhitelist: string[];
    };
    notifications: {
      email: boolean;
      browser: boolean;
      mobile: boolean;
      webhooks: string[];
    };
    integrations: {
      sso: {
        enabled: boolean;
        provider?: string;
        config?: any;
      };
      api: {
        enabled: boolean;
        rateLimit: number;
        webhookUrl?: string;
      };
    };
  };
  metadata: {
    notes?: string;
    tags?: string[];
  };
}

const availableFeatures = [
  { id: 'advanced_reporting', name: 'Advanced Reporting', description: 'Enhanced analytics and custom reports' },
  { id: 'api_access', name: 'API Access', description: 'Full REST API access for integrations' },
  { id: 'custom_branding', name: 'Custom Branding', description: 'White-label customization options' },
  { id: 'sso_integration', name: 'SSO Integration', description: 'Single Sign-On with external providers' },
  { id: 'priority_support', name: 'Priority Support', description: 'Dedicated support channel' },
  { id: 'data_export', name: 'Data Export', description: 'Bulk data export capabilities' },
  { id: 'audit_logs', name: 'Audit Logs', description: 'Detailed activity logging' },
  { id: 'custom_fields', name: 'Custom Fields', description: 'User-defined data fields' },
  { id: 'workflow_automation', name: 'Workflow Automation', description: 'Automated business processes' },
  { id: 'advanced_permissions', name: 'Advanced Permissions', description: 'Granular access control' }
];

const themeOptions = [
  { value: 'light', name: 'Light Theme' },
  { value: 'dark', name: 'Dark Theme' },
  { value: 'auto', name: 'Auto (System)' },
  { value: 'custom', name: 'Custom Theme' }
];

export const TenantSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('features');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTenantSettings(id);
    }
  }, [id]);

  const fetchTenantSettings = async (tenantId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/platform/tenants/${tenantId}`);

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tenant settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      const response = await api.put(`/platform/tenants/${settings.id}`, {
        config: settings.config,
        metadata: settings.metadata
      });

      if (response.data.success) {
        setHasChanges(false);
        // Show success message
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureToggle = (featureId: string) => {
    if (!settings) return;

    const newFeatures = settings.config.features.includes(featureId)
      ? settings.config.features.filter(f => f !== featureId)
      : [...settings.config.features, featureId];

    setSettings({
      ...settings,
      config: {
        ...settings.config,
        features: newFeatures
      }
    });
    setHasChanges(true);
  };

  const handleConfigChange = (path: string, value: any) => {
    if (!settings) return;

    const keys = path.split('.');
    const newConfig = { ...settings.config };
    let current: any = newConfig;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;

    setSettings({
      ...settings,
      config: newConfig
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tenant settings...</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Settings Not Found</h2>
          <p className="text-gray-600 mb-4">Unable to load tenant settings.</p>
          <button 
            onClick={() => navigate('/platform/tenants')}
            className="btn-primary"
          >
            Back to Firms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(`/platform/tenants/${id}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Settings className="w-6 h-6 text-primary-600 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Tenant Settings</h1>
                  <p className="text-gray-600">{settings.name}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {hasChanges && (
                <span className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
                  Unsaved changes
                </span>
              )}
              <button
                onClick={() => fetchTenantSettings(settings.id)}
                className="btn-secondary"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="btn-primary"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-t border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'features', name: 'Features', icon: ToggleLeft },
                { id: 'customization', name: 'Customization', icon: Palette },
                { id: 'security', name: 'Security', icon: Shield },
                { id: 'notifications', name: 'Notifications', icon: Bell },
                { id: 'integrations', name: 'Integrations', icon: Globe }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'features' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Feature Configuration</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {availableFeatures.map((feature) => (
                <div key={feature.id} className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0 mt-1">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.config.features.includes(feature.id)}
                        onChange={() => handleFeatureToggle(feature.id)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{feature.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'customization' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Customization Settings</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                <select
                  value={settings.config.customization.theme}
                  onChange={(e) => handleConfigChange('customization.theme', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {themeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <input
                  type="color"
                  value={settings.config.customization.primaryColor || '#3B82F6'}
                  onChange={(e) => handleConfigChange('customization.primaryColor', e.target.value)}
                  className="w-20 h-10 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Custom CSS</label>
                <textarea
                  value={settings.config.customization.customCss || ''}
                  onChange={(e) => handleConfigChange('customization.customCss', e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder="/* Custom CSS styles */"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Security Settings</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Password Policy</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Length</label>
                    <input
                      type="number"
                      value={settings.config.security.passwordPolicy.minLength}
                      onChange={(e) => handleConfigChange('security.passwordPolicy.minLength', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      min="6"
                      max="50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
                    <input
                      type="number"
                      value={settings.config.security.sessionTimeout}
                      onChange={(e) => handleConfigChange('security.sessionTimeout', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      min="5"
                      max="1440"
                    />
                  </div>
                </div>
                
                <div className="mt-4 space-y-2">
                  {[
                    { key: 'requireUppercase', label: 'Require uppercase letters' },
                    { key: 'requireLowercase', label: 'Require lowercase letters' },
                    { key: 'requireNumbers', label: 'Require numbers' },
                    { key: 'requireSymbols', label: 'Require symbols' }
                  ].map((rule) => (
                    <label key={rule.key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.config.security.passwordPolicy[rule.key as keyof typeof settings.config.security.passwordPolicy] as boolean}
                        onChange={(e) => handleConfigChange(`security.passwordPolicy.${rule.key}`, e.target.checked)}
                        className="mr-3 rounded"
                      />
                      <span className="text-sm text-gray-700">{rule.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.config.security.mfaRequired}
                    onChange={(e) => handleConfigChange('security.mfaRequired', e.target.checked)}
                    className="mr-3 rounded"
                  />
                  <span className="font-medium text-gray-900">Require Multi-Factor Authentication</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Notification Settings</h3>
            
            <div className="space-y-4">
              {[
                { key: 'email', label: 'Email Notifications', icon: Mail },
                { key: 'browser', label: 'Browser Notifications', icon: Bell },
                { key: 'mobile', label: 'Mobile Push Notifications', icon: Bell }
              ].map((notification) => {
                const Icon = notification.icon;
                return (
                  <label key={notification.key} className="flex items-center p-4 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      checked={settings.config.notifications[notification.key as keyof typeof settings.config.notifications] as boolean}
                      onChange={(e) => handleConfigChange(`notifications.${notification.key}`, e.target.checked)}
                      className="mr-3 rounded"
                    />
                    <Icon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="font-medium text-gray-900">{notification.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Integration Settings</h3>
            
            <div className="space-y-6">
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Key className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Single Sign-On (SSO)</h4>
                      <p className="text-sm text-gray-600">Enable SSO authentication</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.config.integrations.sso.enabled}
                      onChange={(e) => handleConfigChange('integrations.sso.enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">API Access</h4>
                      <p className="text-sm text-gray-600">Enable REST API access</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.config.integrations.api.enabled}
                      onChange={(e) => handleConfigChange('integrations.api.enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
                
                {settings.config.integrations.api.enabled && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rate Limit (requests/hour)</label>
                    <input
                      type="number"
                      value={settings.config.integrations.api.rateLimit}
                      onChange={(e) => handleConfigChange('integrations.api.rateLimit', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      min="100"
                      max="10000"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
